/**
 * Customer Contract Service
 * Ügyfél szerződések kezelése órakeret funkcióval
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { 
  CustomerContract, 
  ContractFormData, 
  ContractStats,
  HourPool 
} from '../types/customerContract';

const COLLECTION_NAME = 'customerContracts';

/**
 * Szerződés létrehozása
 */
export const createContract = async (
  data: ContractFormData,
  organizationId: string,
  userId: string,
  userName: string
): Promise<string> => {
  try {
    // Szerződésszám generálása
    const contractNumber = await generateContractNumber(organizationId);
    
    const contractData: Omit<CustomerContract, 'id'> = {
      organizationId,
      customerId: data.customerId,
      customerName: '', // Ezt majd denormalizáljuk
      contractNumber,
      contractName: data.contractName,
      contractType: data.contractType,
      status: 'active',
      services: data.services,
      startDate: Timestamp.fromDate(data.startDate),
      ...(data.endDate && { endDate: Timestamp.fromDate(data.endDate) }),
      monthlyFee: data.monthlyFee,
      billingFrequency: data.billingFrequency,
      currency: data.currency || 'HUF',
      ...(data.notes && { notes: data.notes }),
      ...(data.terms && { terms: data.terms }),
      ...(data.autoRenew !== undefined && { autoRenew: data.autoRenew }),
      ...(data.renewalNoticeDays && { renewalNoticeDays: data.renewalNoticeDays }),
      ...(data.generatedFrom && { 
        generatedFrom: {
          ...data.generatedFrom,
          generatedAt: serverTimestamp() as Timestamp
        }
      }),
      createdAt: serverTimestamp() as Timestamp,
      updatedAt: serverTimestamp() as Timestamp,
      createdBy: userId,
      createdByName: userName
    };

    const docRef = await addDoc(collection(db, COLLECTION_NAME), contractData);
    return docRef.id;
  } catch (error) {
    console.error('Error creating contract:', error);
    throw error;
  }
};

/**
 * Szerződés generálása template-ből
 */
export const generateContractFromTemplate = async (
  templateId: string,
  customerId: string,
  customerName: string,
  organizationId: string,
  userId: string,
  userName: string,
  overrides?: Partial<ContractFormData>
): Promise<string> => {
  try {
    // Template lekérése
    const templateRef = doc(db, 'contractTemplates', templateId);
    const templateSnap = await getDoc(templateRef);
    
    if (!templateSnap.exists()) {
      throw new Error('Template not found');
    }
    
    const template = templateSnap.data();
    
    // Szerződés adatok összeállítása template alapján
    const contractData: ContractFormData = {
      customerId,
      contractName: overrides?.contractName || `${template.name} - ${customerName}`,
      contractType: overrides?.contractType || template.contractType,
      services: overrides?.services || template.services,
      startDate: overrides?.startDate || new Date(),
      endDate: overrides?.endDate,
      monthlyFee: overrides?.monthlyFee || template.defaultMonthlyFee,
      billingFrequency: overrides?.billingFrequency || template.defaultBillingFrequency,
      currency: overrides?.currency || template.defaultCurrency,
      terms: overrides?.terms || template.defaultTerms,
      autoRenew: overrides?.autoRenew !== undefined ? overrides.autoRenew : template.defaultAutoRenew,
      renewalNoticeDays: overrides?.renewalNoticeDays || template.defaultRenewalNoticeDays,
      generatedFrom: {
        type: 'template',
        sourceId: templateId,
        sourceName: template.name
      }
    };
    
    return await createContract(contractData, organizationId, userId, userName);
  } catch (error) {
    console.error('Error generating contract from template:', error);
    throw error;
  }
};

/**
 * Szerződés generálása ajánlatból
 */
export const generateContractFromQuotation = async (
  quotationId: string,
  quotationName: string,
  customerId: string,
  customerName: string,
  services: any[],
  organizationId: string,
  userId: string,
  userName: string
): Promise<string> => {
  try {
    const contractData: ContractFormData = {
      customerId,
      contractName: `Szerződés - ${quotationName}`,
      contractType: 'fixed_term',
      services,
      startDate: new Date(),
      monthlyFee: services.reduce((sum, s) => sum + s.totalPrice, 0),
      billingFrequency: 'monthly',
      currency: 'HUF',
      generatedFrom: {
        type: 'quotation',
        sourceId: quotationId,
        sourceName: quotationName
      }
    };
    
    return await createContract(contractData, organizationId, userId, userName);
  } catch (error) {
    console.error('Error generating contract from quotation:', error);
    throw error;
  }
};

/**
 * Szerződés frissítése
 */
export const updateContract = async (
  contractId: string,
  data: Partial<ContractFormData>
): Promise<void> => {
  try {
    const contractRef = doc(db, COLLECTION_NAME, contractId);
    
    const updateData: any = {
      ...data,
      updatedAt: serverTimestamp()
    };

    // Dátumok konvertálása
    if (data.startDate) {
      updateData.startDate = Timestamp.fromDate(data.startDate);
    }
    if (data.endDate) {
      updateData.endDate = Timestamp.fromDate(data.endDate);
    }

    await updateDoc(contractRef, updateData);
  } catch (error) {
    console.error('Error updating contract:', error);
    throw error;
  }
};

/**
 * Szerződés törlése (soft delete - státusz változtatás)
 */
export const deleteContract = async (contractId: string): Promise<void> => {
  try {
    const contractRef = doc(db, COLLECTION_NAME, contractId);
    await updateDoc(contractRef, {
      status: 'terminated',
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error deleting contract:', error);
    throw error;
  }
};

/**
 * Szerződés lekérése ID alapján
 */
export const getContract = async (
  contractId: string,
  organizationId: string
): Promise<CustomerContract | null> => {
  try {
    const contractRef = doc(db, COLLECTION_NAME, contractId);
    const contractSnap = await getDoc(contractRef);

    if (!contractSnap.exists()) {
      return null;
    }

    const data = contractSnap.data();
    
    // Ellenőrizzük, hogy a megfelelő szervezethez tartozik-e
    if (data.organizationId !== organizationId) {
      return null;
    }

    return {
      id: contractSnap.id,
      ...data
    } as CustomerContract;
  } catch (error) {
    console.error('Error getting contract:', error);
    throw error;
  }
};

/**
 * Összes szerződés lekérése szervezethez
 */
export const getContracts = async (
  organizationId: string
): Promise<CustomerContract[]> => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('organizationId', '==', organizationId),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as CustomerContract));
  } catch (error) {
    console.error('Error getting contracts:', error);
    throw error;
  }
};

/**
 * Ügyfél szerződései
 */
export const getCustomerContracts = async (
  customerId: string,
  organizationId: string
): Promise<CustomerContract[]> => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('organizationId', '==', organizationId),
      where('customerId', '==', customerId),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as CustomerContract));
  } catch (error) {
    console.error('Error getting customer contracts:', error);
    throw error;
  }
};

/**
 * Órakeret frissítése (task időkövetés után)
 */
export const updateHourPool = async (
  contractId: string,
  serviceIndex: number,
  hoursUsed: number
): Promise<void> => {
  try {
    const contractRef = doc(db, COLLECTION_NAME, contractId);
    const contractSnap = await getDoc(contractRef);
    
    if (!contractSnap.exists()) {
      throw new Error('Contract not found');
    }

    const contract = contractSnap.data() as CustomerContract;
    const service = contract.services[serviceIndex];
    
    if (!service.hourPool) {
      throw new Error('Service does not have hour pool');
    }

    const hourPool = service.hourPool;
    const newUsedHours = hourPool.usedHours + hoursUsed;
    const newRemainingHours = Math.max(0, hourPool.includedHours - newUsedHours);
    const newOverageHours = Math.max(0, newUsedHours - hourPool.includedHours);

    // Frissítjük az órakeretet
    const updatedServices = [...contract.services];
    updatedServices[serviceIndex] = {
      ...service,
      hourPool: {
        ...hourPool,
        usedHours: newUsedHours,
        remainingHours: newRemainingHours,
        overageHours: newOverageHours
      }
    };

    await updateDoc(contractRef, {
      services: updatedServices,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating hour pool:', error);
    throw error;
  }
};

/**
 * Órakeret nullázása (havi/negyedéves/éves)
 */
export const resetHourPool = async (
  contractId: string,
  serviceIndex: number
): Promise<void> => {
  try {
    const contractRef = doc(db, COLLECTION_NAME, contractId);
    const contractSnap = await getDoc(contractRef);
    
    if (!contractSnap.exists()) {
      throw new Error('Contract not found');
    }

    const contract = contractSnap.data() as CustomerContract;
    const service = contract.services[serviceIndex];
    
    if (!service.hourPool) {
      throw new Error('Service does not have hour pool');
    }

    // Nullázzuk az órakeretet
    const updatedServices = [...contract.services];
    updatedServices[serviceIndex] = {
      ...service,
      hourPool: {
        ...service.hourPool,
        usedHours: 0,
        remainingHours: service.hourPool.includedHours,
        overageHours: 0,
        lastResetDate: serverTimestamp() as Timestamp
      }
    };

    await updateDoc(contractRef, {
      services: updatedServices,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error resetting hour pool:', error);
    throw error;
  }
};

/**
 * Task hozzáadása szerződéshez
 */
export const addTaskToContract = async (
  contractId: string,
  taskId: string
): Promise<void> => {
  try {
    const contractRef = doc(db, COLLECTION_NAME, contractId);
    const contractSnap = await getDoc(contractRef);
    
    if (!contractSnap.exists()) {
      throw new Error('Contract not found');
    }

    const contract = contractSnap.data() as CustomerContract;
    const taskIds = contract.taskIds || [];
    
    if (!taskIds.includes(taskId)) {
      taskIds.push(taskId);
      await updateDoc(contractRef, {
        taskIds,
        updatedAt: serverTimestamp()
      });
    }
  } catch (error) {
    console.error('Error adding task to contract:', error);
    throw error;
  }
};

/**
 * Számla hozzáadása szerződéshez
 */
export const addInvoiceToContract = async (
  contractId: string,
  invoiceId: string
): Promise<void> => {
  try {
    const contractRef = doc(db, COLLECTION_NAME, contractId);
    const contractSnap = await getDoc(contractRef);
    
    if (!contractSnap.exists()) {
      throw new Error('Contract not found');
    }

    const contract = contractSnap.data() as CustomerContract;
    const invoiceIds = contract.invoiceIds || [];
    
    if (!invoiceIds.includes(invoiceId)) {
      invoiceIds.push(invoiceId);
      await updateDoc(contractRef, {
        invoiceIds,
        lastBillingDate: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    }
  } catch (error) {
    console.error('Error adding invoice to contract:', error);
    throw error;
  }
};

/**
 * Statisztikák lekérése
 */
export const getContractStats = async (
  organizationId: string
): Promise<ContractStats> => {
  try {
    const contracts = await getContracts(organizationId);
    
    const activeContracts = contracts.filter(c => c.status === 'active');
    
    // Lejáró szerződések (30 napon belül)
    const now = new Date();
    const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    const expiringSoon = activeContracts.filter(c => {
      if (!c.endDate) return false;
      const endDate = c.endDate instanceof Timestamp ? c.endDate.toDate() : c.endDate;
      return endDate <= thirtyDaysFromNow && endDate > now;
    });

    // Havi bevétel
    const totalMonthlyRevenue = activeContracts.reduce((sum, c) => sum + c.monthlyFee, 0);
    
    return {
      totalContracts: contracts.length,
      activeContracts: activeContracts.length,
      expiringSoon: expiringSoon.length,
      totalMonthlyRevenue,
      totalYearlyRevenue: totalMonthlyRevenue * 12
    };
  } catch (error) {
    console.error('Error getting contract stats:', error);
    throw error;
  }
};

/**
 * Szerződésszám generálása
 */
const generateContractNumber = async (organizationId: string): Promise<string> => {
  try {
    const year = new Date().getFullYear();
    const q = query(
      collection(db, COLLECTION_NAME),
      where('organizationId', '==', organizationId),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const count = querySnapshot.size + 1;
    
    return `SZ-${year}-${String(count).padStart(4, '0')}`;
  } catch (error) {
    console.error('Error generating contract number:', error);
    // Fallback
    return `SZ-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 10000)).padStart(4, '0')}`;
  }
};

/**
 * Szerződéses szolgáltatás létrehozása termékből
 * Listaár és egyedi ár kezelésével
 */
export const createContractServiceFromProduct = (
  product: any,
  quantity: number = 1,
  customPrice?: number,
  priceNote?: string,
  hourPool?: any,
  billingType: 'one_time' | 'recurring' = 'recurring' // Alapértelmezetten ismétlődő
): any => {
  const listPrice = product.netPrice || 0;
  const contractPrice = customPrice !== undefined ? customPrice : listPrice;
  const discount = customPrice !== undefined && listPrice > 0 
    ? Math.round(((listPrice - customPrice) / listPrice) * 100) 
    : 0;
  const discountAmount = listPrice - contractPrice;
  
  // Csomag tartalom átmásolása (ha van)
  const packageServices = product.packageDetails?.services?.map((service: any) => ({
    productId: service.productId || service.id,
    productCode: service.productCode || '',
    productName: service.name,
    quantity: service.quantity || 1,
    unit: service.unit,
    hours: service.hours,
    isIncluded: service.isIncluded !== false // Alapértelmezetten benne van
  }));
  
  return {
    productId: product.id,
    productName: product.name,
    productCode: product.code,
    productType: product.type, // product | service | package
    description: product.description,
    quantity,
    billingType, // Egyszeri vagy ismétlődő díj
    listPrice, // Globális listaár
    contractPrice, // Egyedi szerződéses ár
    unitPrice: contractPrice, // Kompatibilitás
    totalPrice: contractPrice * quantity,
    discount: discount > 0 ? discount : undefined,
    discountAmount: discountAmount > 0 ? discountAmount : undefined,
    taxRate: product.taxRate,
    ...(hourPool && { hourPool }),
    ...(packageServices && packageServices.length > 0 && { packageServices }),
    ...(priceNote && { priceNote })
  };
};
